import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Bat — a storybook fruit bat standing plumply on the ground, wings folded
// like a little cloak at its sides. Chunky toy proportions: one round russet
// body-and-head mass with huge perky ears, big friendly eyes and a snub pink
// nose. Built to animate: the wings and the ears are named so the "flutter"
// loop can throw the wings open and buzz them while the idle keeps the little
// chest breathing and the ears twitching.
//
// Frame: faces +Z, stands on the floor (y = 0). About 0.35 m tall at the ears.
//
// Rig layout (flat, no hierarchy — each named node moves about its own pivot):
//   - "body"   : the round torso, belly and feet. Breathes, hops in a flutter.
//   - "head"   : the face (eyes, muzzle, nose). Pivot low so tilts read as a
//                cocked head on the round body.
//   - "ear-l" / "ear-r" : the huge ears. Pivot where each meets the crown.
//   - "wing-l" / "wing-r" : the folded wings. Pivot at each shoulder, so a
//                flap swings them out and up like opening a cloak.

const FUR = "#9a6240";         // warm russet fruit-bat fur
const FUR_DARK = "#7a4b2f";    // shaded russet for the wing arms
const WING = "#5e3a2b";        // the leathery wing membrane, deepest of the browns
const CREAM = "#f6efe3";       // belly and muzzle
const PINK = "#e08d76";        // snub nose and inner ears — warm, friendly pink
const EYE_WHITE = "#faf6ec";   // eye whites
const PUPIL = "#33282e";       // big friendly pupils

// The pivots the animations rotate the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0.13, 0];
const NECK_PIVOT: [number, number, number] = [0, 0.19, 0.03];
const EAR_L_PIVOT: [number, number, number] = [-0.06, 0.3, 0.0];
const EAR_R_PIVOT: [number, number, number] = [0.06, 0.3, 0.0];
const SHOULDER_L: [number, number, number] = [-0.09, 0.2, 0.0];
const SHOULDER_R: [number, number, number] = [0.09, 0.2, 0.0];

// One big friendly eye: white ball, big pupil slightly forward, catch-light.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.029, 10, 8), color: EYE_WHITE,
      position: [x, 0.25, 0.095], name: "head" },
    { geometry: new THREE.SphereGeometry(0.019, 8, 6), color: PUPIL,
      position: [x * 0.97, 0.248, 0.115], name: "head" },
    { geometry: new THREE.SphereGeometry(0.0065, 6, 5), color: EYE_WHITE,
      position: [x * 0.97 + 0.007, 0.257, 0.129], name: "head" },
  ];
}

// One huge ear: a tall russet cone with a pink lining facing front.
function ear(name: string, x: number): Part[] {
  return [
    { geometry: new THREE.ConeGeometry(0.045, 0.115, 7), color: FUR,
      position: [x * 1.1, 0.345, -0.005], rotation: [0, 0, -Math.sign(x) * 0.3], name },
    { geometry: new THREE.ConeGeometry(0.026, 0.07, 6), color: PINK,
      position: [x * 1.13, 0.335, 0.018], rotation: [-0.15, 0, -Math.sign(x) * 0.3], name },
  ];
}

// One folded wing: a shoulder knuckle, a long membrane panel hugging the side
// like a cloak, and a tiny thumb-claw hooked over the top.
function wing(name: string, x: number): Part[] {
  const s = Math.sign(x);
  return [
    // Shoulder / wing-arm knuckle.
    { geometry: new THREE.SphereGeometry(0.045, 9, 7), color: FUR_DARK,
      position: [s * 0.095, 0.185, 0.0], scale: [0.75, 1.0, 0.9], name },
    // The folded membrane, draped down the flank and wrapped to its curve.
    { geometry: new THREE.SphereGeometry(0.085, 11, 9), color: WING,
      position: [s * 0.105, 0.115, -0.025], scale: [0.38, 1.3, 0.9],
      rotation: [0, s * 0.22, -s * 0.14], name },
    // The little thumb claw hooked over the shoulder knuckle.
    { geometry: new THREE.SphereGeometry(0.014, 6, 5), color: PINK,
      position: [s * 0.1, 0.222, 0.03], name },
  ];
}

export const bat = defineModel({
  id: "bat",
  batchId: "mess",
  name: "Bat",
  tags: ["mess", "cute", "animated", "creature", "animal", "bat", "fruit-bat", "mascot"],
  summary: "A round fruit bat on the ground, wings folded, ears up, ready to flutter.",
  description:
    "A storybook fruit bat standing plumply on the ground: one round russet " +
    "body-and-head mass with a cream belly, huge pink-lined ears, big friendly " +
    "eyes and a snub pink nose, its leathery wings folded down its sides like " +
    "a little cloak. The head, each ear and each wing are separately posable; " +
    "the flutter loop throws the wings open and buzzes them while the bat hops " +
    "in place, and the idle keeps the chest breathing and the ears twitching.",
  author: "models",
  footprint: 0.3,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── body (one plump mass) ──────────────────────────────────────────
      // The round torso, squatting almost on the floor.
      { geometry: new THREE.SphereGeometry(0.115, 12, 10), color: FUR,
        position: [0, 0.125, 0], scale: [1.0, 1.05, 0.95], name: "body" },
      // Cream belly patch.
      { geometry: new THREE.SphereGeometry(0.07, 10, 8), color: CREAM,
        position: [0, 0.105, 0.07], scale: [1.0, 1.15, 0.6], name: "body" },
      // Two tiny feet peeking out under the belly.
      { geometry: new THREE.SphereGeometry(0.026, 7, 5), color: FUR_DARK,
        position: [-0.045, 0.02, 0.08], scale: [1.0, 0.7, 1.5], name: "body" },
      { geometry: new THREE.SphereGeometry(0.026, 7, 5), color: FUR_DARK,
        position: [0.045, 0.02, 0.08], scale: [1.0, 0.7, 1.5], name: "body" },
      // ── head (fused onto the body, oversized) ──────────────────────────
      { geometry: new THREE.SphereGeometry(0.095, 14, 12), color: FUR,
        position: [0, 0.235, 0.015], scale: [1.1, 0.95, 0.95], name: "head" },
      // Cream muzzle with the snub pink nose.
      { geometry: new THREE.SphereGeometry(0.042, 9, 7), color: CREAM,
        position: [0, 0.212, 0.09], scale: [1.1, 0.75, 0.7], name: "head" },
      { geometry: new THREE.SphereGeometry(0.017, 8, 6), color: PINK,
        position: [0, 0.228, 0.122], scale: [1.25, 0.85, 0.8], name: "head" },
      ...eye(-0.048),
      ...eye(0.048),
      // ── the huge ears ──────────────────────────────────────────────────
      ...ear("ear-l", -0.06),
      ...ear("ear-r", 0.06),
      // ── the folded wings ───────────────────────────────────────────────
      ...wing("wing-l", -1),
      ...wing("wing-r", 1),
    ];
  },
  animations: [
    // 1. Idle — a slow breath: the round body swells, the head cocks a hair
    //    and the big ears twitch one after the other, always listening.
    {
      id: "idle",
      label: "Idle breathing",
      duration: 3.2,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: 1, ease: "sine" },
            { t: 1.6, value: 1.035, ease: "sine" },
            { t: 3.2, value: 1, ease: "sine" },
          ],
        },
        {
          target: "head", pivot: NECK_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.6, value: [0, 0.008, 0], ease: "sine" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0.03], ease: "sine" },
            { t: 1.6, value: [0.03, 0, -0.03], ease: "sine" },
            { t: 3.2, value: [0, 0, 0.03], ease: "sine" },
          ],
        },
        {
          target: "ear-l", pivot: EAR_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.2, value: [0, 0, 0], ease: "sine" },
            { t: 1.4, value: [0.15, 0, 0.12], ease: "easeOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeIn" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "ear-r", pivot: EAR_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 2.6, value: [0, 0, 0], ease: "sine" },
            { t: 2.8, value: [0.15, 0, -0.12], ease: "easeOut" },
            { t: 3.0, value: [0, 0, 0], ease: "easeIn" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          // The folded wings settle with the breath.
          target: "wing-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.6, value: [0, 0, -0.06], ease: "sine" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "wing-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.6, value: [0, 0, 0.06], ease: "sine" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Flutter — the wings throw open and buzz in quick beats while the
    //    whole bat hops in place, ears pinned back with the effort, then
    //    everything folds neatly shut again.
    {
      id: "flutter",
      label: "Wing flutter",
      duration: 2.6,
      loop: true,
      tracks: [
        {
          target: "wing-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.35, value: [0, 0, -1.25], ease: "easeOut" },
            { t: 0.55, value: [0, 0, -0.55], ease: "easeInOut" },
            { t: 0.75, value: [0, 0, -1.35], ease: "easeInOut" },
            { t: 0.95, value: [0, 0, -0.55], ease: "easeInOut" },
            { t: 1.15, value: [0, 0, -1.35], ease: "easeInOut" },
            { t: 1.35, value: [0, 0, -0.55], ease: "easeInOut" },
            { t: 1.55, value: [0, 0, -1.25], ease: "easeInOut" },
            { t: 2.0, value: [0, 0, -1.1], ease: "easeInOut" },
            { t: 2.6, value: [0, 0, 0], ease: "easeIn" },
          ],
        },
        {
          target: "wing-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.35, value: [0, 0, 1.25], ease: "easeOut" },
            { t: 0.55, value: [0, 0, 0.55], ease: "easeInOut" },
            { t: 0.75, value: [0, 0, 1.35], ease: "easeInOut" },
            { t: 0.95, value: [0, 0, 0.55], ease: "easeInOut" },
            { t: 1.15, value: [0, 0, 1.35], ease: "easeInOut" },
            { t: 1.35, value: [0, 0, 0.55], ease: "easeInOut" },
            { t: 1.55, value: [0, 0, 1.25], ease: "easeInOut" },
            { t: 2.0, value: [0, 0, 1.1], ease: "easeInOut" },
            { t: 2.6, value: [0, 0, 0], ease: "easeIn" },
          ],
        },
        {
          // The whole bat hops with the wing beats.
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.55, value: [0, 0.03, 0], ease: "sine" },
            { t: 0.95, value: [0, 0.055, 0], ease: "sine" },
            { t: 1.35, value: [0, 0.03, 0], ease: "sine" },
            { t: 1.8, value: [0, 0.04, 0], ease: "sine" },
            { t: 2.6, value: [0, 0, 0], ease: "bounce" },
          ],
        },
        {
          // The head rides the hop.
          target: "head", pivot: NECK_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.55, value: [0, 0.03, 0], ease: "sine" },
            { t: 0.95, value: [0, 0.055, 0], ease: "sine" },
            { t: 1.35, value: [0, 0.03, 0], ease: "sine" },
            { t: 1.8, value: [0, 0.04, 0], ease: "sine" },
            { t: 2.6, value: [0, 0, 0], ease: "bounce" },
          ],
        },
        {
          // Ears pinned back with the effort.
          target: "ear-l", pivot: EAR_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.45, value: [-0.3, 0, 0.1], ease: "easeOut" },
            { t: 1.7, value: [-0.3, 0, 0.1], ease: "easeInOut" },
            { t: 2.6, value: [0, 0, 0], ease: "easeIn" },
          ],
        },
        {
          target: "ear-r", pivot: EAR_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.45, value: [-0.3, 0, -0.1], ease: "easeOut" },
            { t: 1.7, value: [-0.3, 0, -0.1], ease: "easeInOut" },
            { t: 2.6, value: [0, 0, 0], ease: "easeIn" },
          ],
        },
      ],
    },
  ],
});
